"use client";

import type { CSSProperties } from "react";
import { Avatar } from "@/components/design-system/ArtistDesignSystem";
import type { LibraryProfile } from "./library-data";

export interface ProfileHeaderProps {
	readonly profile: LibraryProfile;
	readonly hideSlogan: boolean;
	readonly onAvatarTap?: (() => void) | undefined;
}

const wrapStyle: CSSProperties = {
	display: "grid",
	gap: 16,
	justifyItems: "center",
	textAlign: "center",
};

const nameRowStyle: CSSProperties = {
	display: "flex",
	alignItems: "center",
	gap: 8,
	justifyContent: "center",
};

/**
 * ProfileHeader — avatar + display name + tier label + demographic·location +
 * Following/Fans/Likes stats + welcome message/bio. Mirrors `ProfileHeader`
 * on iOS (68pt circular avatar, 24pt bold rounded name, capsule tier label,
 * subheadline demographic line, 32pt-spaced stat row, footnote slogan).
 */
export function ProfileHeader({
	profile,
	hideSlogan,
	onAvatarTap,
}: Readonly<ProfileHeaderProps>) {
	const demographicLine = [profile.demographic, profile.location]
		.map((part) => part.trim())
		.filter((part) => part.length > 0)
		.join(" · ");

	const slogan =
		profile.bio !== null && profile.bio.trim().length > 0
			? profile.bio.trim()
			: profile.welcomeMessage;

	const displayName =
		profile.id === "placeholder" || profile.displayName.trim().length === 0
			? "未命名创作者"
			: profile.displayName;

	const avatarProps =
		profile.avatarURL === null
			? {
					name: displayName,
					size: 68,
					className: "library-profile-header__avatar-img",
				}
			: {
					name: displayName,
					src: profile.avatarURL,
					size: 68,
					className: "library-profile-header__avatar-img",
				};

	return (
		<div className="library-profile-header" style={wrapStyle}>
			<button
				type="button"
				className="library-profile-header__avatar"
				onClick={onAvatarTap}
				aria-label="查看头像"
				style={{
					border: 0,
					padding: 0,
					background: "transparent",
					borderRadius: "50%",
					cursor: onAvatarTap === undefined ? "default" : "pointer",
				}}
			>
				<Avatar {...avatarProps} />
			</button>

			<div
				className="library-profile-header__identity"
				style={{ display: "grid", gap: 6, justifyItems: "center" }}
			>
				<div style={nameRowStyle}>
					<h2
						className="library-profile-header__name"
						style={{
							margin: 0,
							fontSize: "1.5rem",
							fontWeight: 800,
							letterSpacing: "-0.01em",
							color: "var(--ds-on-media-primary)",
						}}
					>
						{displayName}
					</h2>
					{profile.tierLabel === null ||
					profile.tierLabel.length === 0 ? null : (
						<span
							className="library-profile-header__tier"
							style={{
								padding: "4px 12px",
								borderRadius: "var(--ds-radius-full)",
								fontSize: "0.74rem",
								fontWeight: 700,
								color: "var(--ds-on-media-primary)",
								background:
									"color-mix(in display-p3, var(--ds-on-media-primary) 18%, transparent)",
							}}
						>
							{profile.tierLabel}
						</span>
					)}
				</div>
				{demographicLine.length === 0 ? null : (
					<p
						className="library-profile-header__meta"
						style={{
							margin: 0,
							fontSize: "0.92rem",
							color:
								"color-mix(in display-p3, var(--ds-on-media-primary) 78%, transparent)",
						}}
					>
						{demographicLine}
					</p>
				)}
			</div>

			<div
				className="library-profile-header__stats"
				style={{
					display: "flex",
					gap: 32,
					justifyContent: "center",
					width: "100%",
				}}
			>
				<ProfileStat title="关注" value={profile.followingCount} />
				<ProfileStat title="粉丝" value={profile.fansCount} />
				<ProfileStat title="获赞" value={profile.likesCount} />
			</div>

			<p
				className="library-profile-header__slogan"
				style={{
					margin: 0,
					fontSize: "0.86rem",
					lineHeight: 1.5,
					color:
						"color-mix(in display-p3, var(--ds-on-media-primary) 78%, transparent)",
					maxWidth: 420,
					opacity: hideSlogan ? 0 : 1,
					transition: "opacity 200ms ease",
				}}
			>
				{slogan}
			</p>
		</div>
	);
}

function ProfileStat({
	title,
	value,
}: Readonly<{ title: string; value: number }>) {
	return (
		<div
			style={{ display: "grid", gap: 4, justifyItems: "center", minWidth: 64 }}
		>
			<strong
				style={{
					fontSize: "1rem",
					fontWeight: 650,
					color: "var(--ds-on-media-primary)",
					fontVariantNumeric: "tabular-nums",
				}}
			>
				{formatStat(value)}
			</strong>
			<span
				style={{
					fontSize: "0.74rem",
					color:
						"color-mix(in display-p3, var(--ds-on-media-primary) 70%, transparent)",
				}}
			>
				{title}
			</span>
		</div>
	);
}

function formatStat(value: number): string {
	if (value >= 10000) return `${(value / 10000).toFixed(1)}w`;
	if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
	return value.toLocaleString("zh-CN");
}
